/**
 * IDE-specific preset configurations
 */

import type { IDEType, GeneratedFile } from './types.js';

/**
 * Generate Cursor AI preset files
 */
function generateCursorPreset(): GeneratedFile[] {
  return [
    {
      path: '.cursorrules',
      content: `# Cursor AI Rules for @thenoo/agentkit Project

## Context
This is an AI agent project built with @thenoo/agentkit, a TypeScript SDK for building LLM-powered agents with FinOps tracking.

## Core Packages
- @thenoo/agentkit - Core agent framework
- @thenoo/finops - Budget tracking and cost management
- @thenoo/adapter-openai - OpenAI API integration
- @thenoo/adapter-azure - Azure OpenAI integration

## Code Style
- Use TypeScript with strict mode
- Prefer async/await over promises
- Use Zod for schema validation
- Follow ESM import conventions (no .js extensions)

## Agent Development
- Always wrap tools with Zod schemas for input/output validation
- Use createBudgetManager() from @thenoo/finops for cost tracking
- Wrap adapters with withFinOps() for automatic budget enforcement
- Listen to agent events (tool:call, cost:update, cost:alert)

## FinOps Best Practices
- Set reasonable budget limits (usd, perRequestCents)
- Use 'degrade_model' policy for automatic cost optimization
- Track costs per session with unique sessionId
- Monitor cost:alert events for budget breaches

## Testing
- Write tests with vitest
- Mock external API calls
- Aim for 80%+ coverage

## Common Patterns

### Creating an Agent
\`\`\`typescript
import { createAgent, tool } from '@thenoo/agentkit';
import { createBudgetManager } from '@thenoo/finops';

const agent = createAgent({
  name: 'MyAgent',
  tools: [/* your tools */],
  budget: createBudgetManager({ usd: 10 }),
});
\`\`\`

### Wrapping an Adapter
\`\`\`typescript
import { createOpenAIAdapter, withFinOps } from '@thenoo/adapter-openai';

const adapter = createOpenAIAdapter({ apiKey: '...' });
const wrapped = withFinOps(adapter, {
  sessionId: 'user-123',
  budget: { usd: 5, policy: 'degrade_model' },
});
\`\`\`

### Defining a Tool
\`\`\`typescript
import { tool } from '@thenoo/agentkit';
import { z } from 'zod';

const myTool = tool({
  name: 'toolName',
  description: 'What the tool does',
  input: z.object({ /* schema */ }),
  output: z.object({ /* schema */ }),
  run: async (input) => {
    // Implementation
    return output;
  },
});
\`\`\`
`,
    },
    {
      path: '.vscode/settings.json',
      content: JSON.stringify(
        {
          'typescript.tsdk': 'node_modules/typescript/lib',
          'editor.formatOnSave': true,
          'editor.codeActionsOnSave': {
            'source.fixAll.eslint': true,
          },
        },
        null,
        2
      ),
    },
  ];
}

/**
 * Generate GitHub Copilot preset files
 */
function generateCopilotPreset(): GeneratedFile[] {
  return [
    {
      path: '.github/copilot-instructions.md',
      content: `# GitHub Copilot Instructions

## Project Context
This is an @thenoo/agentkit project for building AI agents with TypeScript.

## Key Packages
- **@thenoo/agentkit** - Core agent framework with tools, memory, and events
- **@thenoo/finops** - Budget management and cost tracking
- **@thenoo/adapter-openai** - OpenAI integration with automatic cost tracking
- **@thenoo/adapter-azure** - Azure OpenAI integration

## Code Generation Guidelines

### Tools
When creating tools, always use Zod schemas:

\`\`\`typescript
import { tool } from '@thenoo/agentkit';
import { z } from 'zod';

const exampleTool = tool({
  name: 'toolName',
  description: 'Clear description of what the tool does',
  input: z.object({
    param1: z.string(),
    param2: z.number(),
  }),
  output: z.object({
    result: z.string(),
  }),
  run: async ({ param1, param2 }) => {
    // Implementation
    return { result: '...' };
  },
});
\`\`\`

### Agents
Always include budget tracking:

\`\`\`typescript
import { createAgent } from '@thenoo/agentkit';
import { createBudgetManager } from '@thenoo/finops';

const agent = createAgent({
  name: 'AgentName',
  tools: [tool1, tool2],
  budget: createBudgetManager({
    usd: 10,
    perRequestCents: 50,
    onExceed: 'halt',
  }),
});
\`\`\`

### FinOps Integration
Wrap adapters for automatic cost tracking:

\`\`\`typescript
import { createOpenAIAdapter, withFinOps } from '@thenoo/adapter-openai';

const adapter = createOpenAIAdapter({
  apiKey: process.env.OPENAI_API_KEY!,
});

const wrapped = withFinOps(adapter, {
  sessionId: 'unique-session-id',
  budget: {
    usd: 5,
    perRequestCents: 25,
    policy: 'degrade_model',
  },
  onAlert: (event) => console.warn('Budget alert:', event),
});
\`\`\`

### Event Handling
Listen to agent events:

\`\`\`typescript
agent.on('tool:call', (event) => {
  console.log(\`Calling tool: \${event.name}\`);
});

agent.on('cost:update', (event) => {
  console.log(\`Cost: $\${event.payload.totalUsd}\`);
});

agent.on('cost:alert', (event) => {
  console.error('Budget exceeded!', event.payload);
});
\`\`\`

## Best Practices
- Use TypeScript strict mode
- Validate all inputs with Zod
- Always set budget limits
- Monitor cost events
- Use async/await
- Handle errors gracefully
- Write tests with vitest
`,
    },
  ];
}

/**
 * Generate VS Code preset files
 */
function generateVSCodePreset(): GeneratedFile[] {
  return [
    {
      path: '.vscode/settings.json',
      content: JSON.stringify(
        {
          'typescript.tsdk': 'node_modules/typescript/lib',
          'editor.formatOnSave': true,
          'editor.codeActionsOnSave': {
            'source.fixAll.eslint': 'explicit',
          },
          'editor.defaultFormatter': 'esbenp.prettier-vscode',
          '[typescript]': {
            'editor.defaultFormatter': 'esbenp.prettier-vscode',
          },
          'typescript.preferences.importModuleSpecifier': 'non-relative',
        },
        null,
        2
      ),
    },
    {
      path: '.vscode/extensions.json',
      content: JSON.stringify(
        {
          recommendations: [
            'esbenp.prettier-vscode',
            'dbaeumer.vscode-eslint',
            'ms-vscode.vscode-typescript-next',
          ],
        },
        null,
        2
      ),
    },
    {
      path: '.vscode/launch.json',
      content: JSON.stringify(
        {
          version: '0.2.0',
          configurations: [
            {
              type: 'node',
              request: 'launch',
              name: 'Debug Agent',
              skipFiles: ['<node_internals>/**'],
              program: '${workspaceFolder}/src/index.ts',
              runtimeArgs: ['--loader', 'tsx'],
              env: {
                NODE_ENV: 'development',
              },
            },
          ],
        },
        null,
        2
      ),
    },
  ];
}

/**
 * Generate IDE preset files based on IDE type
 */
export function generateIDEPreset(ide: IDEType): GeneratedFile[] {
  switch (ide) {
    case 'cursor':
      return generateCursorPreset();
    case 'copilot':
      return generateCopilotPreset();
    case 'vscode':
      return generateVSCodePreset();
    case 'none':
      return [];
    default:
      return [];
  }
}

